import BuildCancelButton from '@/components/build/build-cancel-button';
import BuildLogsSection from '@/components/build/build-logs-section';
import BuildLogViewer from '@/components/build/build-log-viewer';
import BuildStageStepper from '@/components/build/build-stage-stepper';
import { usesOmadDropperStages } from '@/components/build/build-stage-config';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DURATION, EASE, useReducedMotion } from '@/lib/motion-presets';
import { AnimatePresence, motion } from 'motion/react';
import { LoaderIcon, ScrollTextIcon } from 'lucide-react';
import { useState } from 'react';
import type { IBuild } from '@/entities/build.entity';
import {
	$buildSawDropperStage,
	$buildSawPayloadEncryptStage,
	selectSawDropperStage,
	selectSawPayloadEncryptStage,
	type IBuildLiveProgress,
	type IBuildLogLine,
} from '@/entities/build-live.entity';
import { useUnit } from 'effector-react';

interface BuildProgressBannerProps {
	build: IBuild | null | undefined;
	liveProgress?: IBuildLiveProgress | null;
	logs?: IBuildLogLine[];
	showAdminLogs?: boolean;
	onCancel: () => void;
	className?: string;
}

function getProgressLabel(
	build: IBuild,
	liveStage?: string | null,
	liveProgressStatus?: IBuildLiveProgress['progressStatus'],
	sawDropperStage = false,
) {
	const progressStatus = liveProgressStatus ?? build.progressStatus;
	const omadFlow = usesOmadDropperStages(sawDropperStage, liveStage);
	if (progressStatus === 'queued' || (build.inQueue && !build.isBuilding && !liveStage)) return 'Билд в очереди…';
	if (liveStage === 'payload_encrypt') return 'Шифрование payload…';
	if (liveStage === 'dropper') return 'Сборка дроппера…';
	if (progressStatus === 'encrypting' || liveStage === 'encrypt') {
		return omadFlow ? 'Шифрование APK…' : 'Шифрование и дроппер…';
	}
	if (progressStatus === 'stale') return 'Сборка завершилась с ошибкой';
	if (progressStatus === 'building' || build.isBuilding || liveStage) return 'Сборка APK…';
	return 'Билд выполняется…';
}

function getProgressHint(
	build: IBuild,
	liveStage?: string | null,
	liveProgressStatus?: IBuildLiveProgress['progressStatus'],
	sawDropperStage = false,
) {
	const progressStatus = liveProgressStatus ?? build.progressStatus;
	const omadFlow = usesOmadDropperStages(sawDropperStage, liveStage);
	if (liveStage === 'payload_encrypt') return 'Шифрование payload, отмена недоступна';
	if (liveStage === 'dropper') return 'Сборка дроппера, отмена недоступна';
	if (progressStatus === 'encrypting' || liveStage === 'encrypt') {
		return omadFlow ? 'Шифрование, отмена недоступна' : 'Шифрование и дроппер, отмена недоступна';
	}
	if (progressStatus === 'stale') return 'Нажмите «Сбросить», чтобы убрать из списка активных';
	return null;
}

function shouldShowStepper(build: IBuild, liveProgress?: IBuildLiveProgress | null) {
	if (liveProgress?.stage) return true;
	return build.progressStatus !== 'queued' && !(build.inQueue && !build.isBuilding);
}

function BuildProgressBannerContent({
	build,
	liveProgress,
	logs = [],
	showAdminLogs = false,
	onCancel,
	className,
}: {
	build: IBuild;
	liveProgress?: IBuildLiveProgress | null;
	logs?: IBuildLogLine[];
	showAdminLogs?: boolean;
	onCancel: () => void;
	className?: string;
}) {
	const [logsOpen, setLogsOpen] = useState(false);
	const sawDropperMap = useUnit($buildSawDropperStage);
	const sawPayloadEncryptMap = useUnit($buildSawPayloadEncryptStage);
	const currentStage = liveProgress?.stage ?? build.builderStage ?? null;
	const sawDropperStage = selectSawDropperStage(sawDropperMap, build.id);
	const sawPayloadEncryptStage = selectSawPayloadEncryptStage(sawPayloadEncryptMap, build.id);
	const label = getProgressLabel(build, currentStage, liveProgress?.progressStatus, sawDropperStage);
	const hint = getProgressHint(build, currentStage, liveProgress?.progressStatus, sawDropperStage);
	const showStepper = shouldShowStepper(build, liveProgress);

	return (
		<div
			className={cn(
				'rounded-xl border border-chart-5/30 bg-chart-5/10 px-4 py-3 flex flex-col gap-3',
				build.progressStatus === 'stale' && 'border-destructive/30 bg-destructive/10',
				className,
			)}
		>
			<div className='flex items-center gap-3'>
				<div className='relative flex size-10 shrink-0 items-center justify-center rounded-full bg-chart-5/15'>
					<LoaderIcon className={cn('size-5 text-chart-5', build.progressStatus !== 'stale' && 'animate-spin')} />
				</div>

				<div className='min-w-0 flex-1'>
					<p className='font-medium text-sm'>{label}</p>
					<p className='truncate text-xs text-muted-foreground'>{build.appName || build.id}</p>
					{hint ? (
						<p className='text-xs text-muted-foreground mt-1'>{hint}</p>
					) : null}
				</div>

				<div className='flex items-center gap-2 shrink-0'>
					{showAdminLogs ? (
						<Button
							type='button'
							variant='outline'
							size='sm'
							className='h-8 text-xs'
							onClick={() => setLogsOpen(value => !value)}
						>
							<ScrollTextIcon className='size-3.5' />
							{logsOpen ? 'Скрыть логи' : 'Логи'}
						</Button>
					) : null}
					{build.canCancel ? <BuildCancelButton build={build} onCancelled={onCancel} /> : null}
				</div>
			</div>

			{showStepper ? (
				<BuildStageStepper
					currentStage={currentStage}
					sawDropperStage={sawDropperStage}
					sawPayloadEncryptStage={sawPayloadEncryptStage}
					skipEncrypt={Boolean(build.skipEncrypt)}
				/>
			) : null}

			{showAdminLogs && logsOpen ? <BuildLogViewer lines={logs} /> : null}

			{build.hasBuildLog && build.progressStatus === 'stale' ? (
				<BuildLogsSection build={build} admin={showAdminLogs} />
			) : null}
		</div>
	);
}

export default function BuildProgressBanner({
	build,
	liveProgress,
	logs,
	showAdminLogs,
	onCancel,
	className,
}: BuildProgressBannerProps) {
	const reducedMotion = useReducedMotion();

	if (reducedMotion) {
		if (!build) return null;
		return (
			<BuildProgressBannerContent
				build={build}
				liveProgress={liveProgress}
				logs={logs}
				showAdminLogs={showAdminLogs}
				onCancel={onCancel}
				className={className}
			/>
		);
	}

	return (
		<AnimatePresence initial={false}>
			{build ? (
				<motion.div
					key={build.id}
					initial={{ opacity: 0, height: 0 }}
					animate={{ opacity: 1, height: 'auto' }}
					exit={{ opacity: 0, height: 0 }}
					transition={{ duration: DURATION, ease: EASE }}
					className='overflow-hidden'
				>
					<BuildProgressBannerContent
						build={build}
						liveProgress={liveProgress}
						logs={logs}
						showAdminLogs={showAdminLogs}
						onCancel={onCancel}
						className={className}
					/>
				</motion.div>
			) : null}
		</AnimatePresence>
	);
}
